// Client for claude-placement-api — the adaptive placement walk.
//
// One walk per domain (all four math + all four reading): 10 questions
// minimum, 12 maximum, each question
// chosen for how much it tells us about THIS student. Everything that decides
// anything happens server-side: grading, the running estimate, and the level
// the student lands on. The browser only ever sees a question with no answer
// attached, and the level window (how wide the estimate still is).
//
// The lambda rides the 60bjnqzpe4 gateway (2026-08-30, moved off its old
// function URL): npm start -> Development stage (dev alias, $LATEST), builds
// -> Production (prod alias, pinned) — the stage logic lives in
// src/config/api.js like every other endpoint. It identifies the student with
// Cognito GetUser — which accepts an ACCESS token only. Sending the idToken
// used elsewhere in this folder reads as unauthorized, so this client is
// deliberately separate from studentApi(). CORS comes from the gateway's own
// config (localhost:3000 + the prod domains).
import { PLACEMENT_API_URL as PLACEMENT_URL } from '../../config/api';

// Dashboard domain key -> the domain id the placement pool is keyed by.
// All eight domains have pools (reading added 2026-08-27: 239 clones served
// by the same lambda with R&W band edges and a band-2 floor).
export const PLACEMENT_DOMAINS = {
  algebra: 'algebra',
  psda: 'problem-solving-and-data-analysis',
  advanced: 'advanced-math',
  geotrig: 'geometry-and-trigonometry',
  info: 'information-and-ideas',
  craft: 'craft-and-structure',
  expression: 'expression-of-ideas',
  conventions: 'standard-english-conventions',
};

// Reading walks never place into band 1 (the Foundations layer catches those
// students), so their level window starts at 2. Math windows start at 1.
export const READING_PLACEMENT_KEYS = new Set(['info', 'craft', 'expression', 'conventions']);

export const hasPlacement = (domainKey) => !!PLACEMENT_DOMAINS[domainKey];

async function placementApi(auth, action, params = {}) {
  const { user, handleAPIResponse } = auth || {};
  const token = user?.accessToken;
  if (!token) throw new Error('not signed in');
  const body = { action, ...params };
  let res = await fetch(PLACEMENT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify(body),
  });
  if ((res.status === 401 || res.status === 403) && handleAPIResponse) {
    res = await handleAPIResponse(res, body); // refresh the token, retry once
  }
  let data = null;
  try { data = await res.json(); } catch { /* non-json body */ }
  if (!res.ok) {
    const err = new Error(data?.error || `placement ${action} failed (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

// Start a walk, or pick up the one already in progress. The response carries
// the first (or current) question, so this doubles as resume.
export function startWalk(auth, domainKey) {
  return placementApi(auth, 'startWalk', { domain: PLACEMENT_DOMAINS[domainKey] });
}

// Submit one answer. Returns the next question, or `complete: true` with the
// result. The verdict rides along in `answered.correct` — the walk page does
// NOT show it (a placement walk holds a student near 50%, so per-question
// verdicts read as failure and give away how the walk works).
// activeMs (2026-08-30): this question's focused dwell — the walk counts as
// the student's first assignment, so its effort scores with the same
// engagement rules as any question. Integrity (tab-aways, copy/paste, event
// log) rides the SAME saveTelemetry channel a session question uses (the
// useQuestionTelemetry hook posting onto the place: row), not this call.
export function submitAnswer(auth, walkId, recordID, answer, activeMs) {
  return placementApi(auth, 'submitAnswer', {
    walkId, recordID, answer,
    ...(Number(activeMs) > 0 ? { activeMs: Math.round(Number(activeMs)) } : {}),
  });
}

// Read-only status for a domain: the active walk (with its current question),
// else the last finished one, else { status: 'none' }.
export function getWalk(auth, domainKey) {
  return placementApi(auth, 'getWalk', { domain: PLACEMENT_DOMAINS[domainKey] });
}

// Every math domain's walk status in one pass, keyed by dashboard domain key.
// A failed read resolves to null for that domain rather than rejecting, so one
// bad response never blanks the other three cards.
export async function fetchAllWalks(auth) {
  const keys = Object.keys(PLACEMENT_DOMAINS);
  const rows = await Promise.all(keys.map((k) => getWalk(auth, k).catch(() => null)));
  return Object.fromEntries(keys.map((k, i) => [k, rows[i]]));
}

// ---------------------------------------------------------------------------
// Rendering adapter: the served question -> the shape QuestionCard reads.
//
// The lambda sends the question stripped of everything that decides a grade
// (no Answer, no ideal_answer, no trap list, no explanation), so the adapter
// is a pure rename plus two rendering decisions.
//
// `latexMC` is computed here, never taken from the bank. The stored flag
// disagrees with the choices themselves on 92 of the 275 multiple-choice
// clones, and QuestionCard's latexMC path sends any choice WITHOUT $-delimiters
// to InlineMath, which renders a plain English sentence as math. Requiring
// every choice to be delimited keeps mixed prose/math items on the verbatim
// path, where they read correctly.
export function toQuestionCard(q) {
  if (!q) return null;
  const choices = q.answerChoices || [];
  const delimited = (t) => /\$.+\$/.test(String(t || ''));
  return {
    recordID: q.recordID,
    questionText: q.questionText,
    // Reading items split passage (questionText) from stem (question); passing
    // both puts QuestionCard on its English layout: passage left, stem +
    // choices right. Math items have no `question` and render as before.
    question: q.question || undefined,
    isMC: !!q.isMC,
    AnswerChoices: choices,
    latexMC: choices.length > 0 && choices.every((c) => delimited(c.text)),
    // The lambda sends the rendered SVG only; the authoring spec stays server
    // side because a figure description can state the answer outright.
    figure: q.figure?.svg ? { type: 'svg', svg: q.figure.svg } : undefined,
    mainConcept: undefined, // concept names would hint at the method
  };
}
